import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const HERO_DIAGRAM_SRC = 'https://images.happysolutions.hu/Happy-Gastro-System-Diagram-2023.webp';

const MODULES = [
    { id: 'pos',         x: 28, y: 18, w: 14, h: 9,  icon: 'ri-computer-line',        tone: 'orange' },
    { id: 'kitchen',     x: 44, y: 11, w: 15, h: 9,  icon: 'ri-restaurant-2-line',    tone: 'amber'  },
    { id: 'invoice',     x: 58, y: 14, w: 12, h: 8,  icon: 'ri-bill-line',            tone: 'orange' },
    { id: 'nav',         x: 72, y: 18, w: 11, h: 8,  icon: 'ri-shield-check-line',    tone: 'red'    },
    { id: 'finance',     x: 79, y: 30, w: 12, h: 9,  icon: 'ri-line-chart-line',      tone: 'purple' },
    { id: 'courier',     x: 84, y: 47, w: 12, h: 9,  icon: 'ri-e-bike-2-line',        tone: 'red'    },
    { id: 'webshop',     x: 69, y: 57, w: 14, h: 9,  icon: 'ri-shopping-cart-2-line', tone: 'teal'   },
    { id: 'reservation', x: 58, y: 42, w: 12, h: 8,  icon: 'ri-calendar-2-line',      tone: 'pink'   },
    { id: 'crm',         x: 45, y: 53, w: 12, h: 8,  icon: 'ri-user-heart-line',      tone: 'purple' },
    { id: 'marketing',   x: 31, y: 58, w: 14, h: 9,  icon: 'ri-megaphone-line',       tone: 'pink'   },
    { id: 'inventory',   x: 24, y: 42, w: 13, h: 9,  icon: 'ri-archive-2-line',       tone: 'amber'  },
    { id: 'reports',     x: 30, y: 30, w: 13, h: 9,  icon: 'ri-bar-chart-box-line',   tone: 'blue'   },
];

const DEFAULTS = {
    pos:         { label: 'Pultos POS',          description: 'Villámgyors kasszás felület étterembe, kávézóba és kiskereskedelembe – érintőképernyőre optimalizálva.' },
    kitchen:     { label: 'Konyha kijelző',      description: 'Élő rendelés-folyam a konyhára, főző- és kiadópozíciók szerinti sorolással, tételszintű állapotokkal.' },
    invoice:     { label: 'Számlázás',           description: 'NAV-kompatibilis e-számlázás, díjbekérők, sztornó és előleg kezelés, automatikus jóváírások.' },
    nav:         { label: 'NAV adatszolg.',      description: 'Online pénztárgép-, számla- és Áfa-bevallás adatszolgáltatás valós időben az adóhatóság felé.' },
    finance:     { label: 'Pénzügy',             description: 'Banki tranzakciók, kifizetések, követelések, cashflow és vezetői riportok egy nézetben.' },
    courier:     { label: 'Futár & szállítás',   description: 'Saját futárflotta és integrált logisztika (Wolt, GLS, Foodpanda) egy diszpécser felületen.' },
    webshop:     { label: 'Webshop',             description: 'Saját arculatú online rendelési rendszer, mely valós időben szinkronban van a készlettel és menüvel.' },
    reservation: { label: 'Foglalás',            description: 'Asztal- és időpont-foglalás, automata megerősítések, kapacitás- és terem-tervező.' },
    crm:         { label: 'CRM',                 description: 'Ügyfél-, partner-, hűségkártya- és kampánykezelés szegmensekkel és értékesítési csatornákkal.' },
    marketing:   { label: 'Marketing',           description: 'E-mail, SMS és push kampányok, kuponok, automatikus szegmentálás vásárlási előzmények alapján.' },
    inventory:   { label: 'Készlet & raktár',    description: 'Cikktörzs, többtelepes raktárkészlet, leltár, beszerzés és receptúra-alapú anyagfelhasználás.' },
    reports:     { label: 'Riportok & BI',       description: 'Vezetői dashboard, valós idejű forgalom, marzs, top termékek és prediktív elemzések.' },
};

const SystemDiagram = ({ className = '', variant = 'inline', alt }) => {
    const { t } = useTranslation();
    const [activeId, setActiveId] = useState(null);

    const modules = useMemo(() => MODULES.map((m) => {
        const d = DEFAULTS[m.id] || { label: m.id, description: '' };

        return {
            ...m,
            label: t(`premium.home.diagram.${m.id}.label`, { defaultValue: d.label }),
            description: t(`premium.home.diagram.${m.id}.description`, { defaultValue: d.description }),
        };
    }), [t]);

    const active = activeId ? modules.find((m) => m.id === activeId) : null;
    const isFullscreen = variant === 'fullscreen';
    const tipOnTop = !!active && active.y > 50;

    const tipStyle = active
        ? {
            left: `${Math.min(92, Math.max(8, active.x + (active.w / 2)))}%`,
            top: `${Math.min(88, Math.max(12, active.y + (active.h / 2)))}%`,
        }
        : undefined;

    return (
        <div
            className={`hgd hgd--${variant} ${activeId ? 'is-hovering' : ''} ${className}`}
            onMouseLeave={() => setActiveId(null)}
        >
            <div className="hgd__frame">
                <img
                    className="hgd__image"
                    src={HERO_DIAGRAM_SRC}
                    alt={alt || t('premium.home.heroDiagramAlt', { defaultValue: 'Happy Solutions rendszer áttekintő diagram' })}
                    draggable={false}
                />

                <div
                    className={`hgd__spotlight${active ? ' is-visible' : ''}`}
                    aria-hidden="true"
                    style={active ? {
                        background: `radial-gradient(circle at ${active.x + (active.w / 2)}% ${active.y + (active.h / 2)}%, rgba(255,255,255,.20) 0%, rgba(248,111,3,.14) 11%, rgba(8,11,24,.12) 24%, rgba(8,11,24,.54) 58%, rgba(8,11,24,.76) 100%)`,
                    } : undefined}
                />

                {modules.map((m) => {
                    const isActive = activeId === m.id;
                    const isDimmed = !!activeId && !isActive;

                    return (
                        <button
                            key={m.id}
                            type="button"
                            className={`hgd__spot hgd__spot--${m.tone}${isActive ? ' is-active' : ''}${isDimmed ? ' is-dimmed' : ''}`}
                            style={{ left: `${m.x}%`, top: `${m.y}%`, width: `${m.w}%`, height: `${m.h}%` }}
                            onMouseEnter={() => setActiveId(m.id)}
                            onFocus={() => setActiveId(m.id)}
                            onBlur={() => setActiveId((curr) => (curr === m.id ? null : curr))}
                            aria-label={`${m.label}: ${m.description}`}
                        >
                            <span className="hgd__spot-core" aria-hidden="true">
                                <i className={m.icon} />
                            </span>
                            <span className="hgd__spot-label">{m.label}</span>
                        </button>
                    );
                })}

                {active && (
                    <div
                        className={`hgd__tooltip ${tipOnTop ? 'is-above' : 'is-below'}`}
                        style={tipStyle}
                        role="status"
                        aria-live="polite"
                    >
                        <div className="hgd__tooltip-head">
                            <i className={active.icon} aria-hidden="true" />
                            <strong>{active.label}</strong>
                        </div>
                        <p>{active.description}</p>
                    </div>
                )}

                {isFullscreen && (
                    <div className="hgd__pulse" aria-hidden="true" />
                )}
            </div>
        </div>
    );
};

export default SystemDiagram;


